import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";

const TrackUploadProgress = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("PENDING");
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  // Ref to the logs container
  const logsContainerRef = useRef(null);

  useEffect(() => {
    if (!uuid) {
      setError("Missing UUID");
      return;
    }

    const eventSource = new EventSource(
      `${process.env.REACT_APP_API_URI}/api/poll-stage1?uuid=${uuid}`
    );

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setLogs(data.logs);
    };

    eventSource.addEventListener("log", (event) => {
      const data = JSON.parse(event.data);
      setStatus("Your video is being processed..");
      setLogs(data.logs);
    });

    eventSource.addEventListener("done", (event) => {
      const data = JSON.parse(event.data);
      setStatus("DONE");
      setLogs(data.logs);
      eventSource.close(); // Close the connection

      navigate(`/identify-speakers/${uuid}`);
    });

    eventSource.addEventListener("error", (event) => {
      const data = JSON.parse(event.data);
      setStatus("ERROR");
      setLogs(data.logs);
      eventSource.close(); // Close the connection
    });

    eventSource.onerror = (err) => {
      console.error("SSE connection error:", err);
      setError("An error occurred while connecting to the server.");
      eventSource.close();
    };

    // Cleanup on component unmount
    return () => {
      eventSource.close();
    };
  }, [uuid, navigate]);

  useEffect(() => {
    // Scroll to the bottom whenever logs are updated
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop =
        logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <Container maxWidth="md" style={{ marginTop: "50px" }}>
      <Box>
        <Typography variant="h5">Track upload progress</Typography>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : status === "PENDING" ? (
          <Box>
            <CircularProgress />
            <Typography variant="body1">Checking for updates...</Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="h6">Status: {status}</Typography>
            {status === "ERROR" && (
              <Alert severity="error" variant="filled">
                There was an error processing your file. Please send your video
                to
                <a href="mailto:dominique.mb@souvenons.com">
                  dominique.mb@souvenons.com
                </a>{" "}
                for us to upload it manually.
              </Alert>
            )}

            <Typography variant="body1">Logs:</Typography>
            <Box
              ref={logsContainerRef}
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #ccc",
                padding: "10px",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              {logs.map((log, index) => (
                <Typography key={index} variant="body2">
                  {log}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default TrackUploadProgress;
